import React, { useState, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './AdminLayout.css';

const AdminLayout = memo(({ children }) => {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { user, signOut, profileData } = useAuth();

    const menuItems = [
        { path: '/admin', icon: 'bx-grid-alt', label: 'Dashboard', exact: true },
        { path: '/admin/users', icon: 'bx-user', label: 'Users' },
        { path: '/admin/referrals', icon: 'bx-gift', label: 'Referrals' },
        { path: '/admin/settings', icon: 'bx-cog', label: 'Settings' },
        // Profile is reached from the admin chip in the top bar, not the sidebar.
    ];

    const isActive = (path, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    const handleLogout = async () => {
        await signOut();
        navigate('/admin/login');
    };

    const toggleSidebar = () => {
        setSidebarCollapsed(!sidebarCollapsed);
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    return (
        <div className={`admin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Sidebar */}
            <aside className={`admin-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                <div className="sidebar-header">
                    <div className="brand">
                        {!sidebarCollapsed && <span className="brand-text">Sigma Bank</span>}
                    </div>
                </div>

                <nav className="sidebar-nav">
                    {menuItems.map((item) => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-item ${isActive(item.path, item.exact) ? 'active' : ''}`}
                            onClick={() => setMobileMenuOpen(false)}
                            title={sidebarCollapsed ? item.label : ''}
                        >
                            <i className={`bx ${item.icon} nav-icon`}></i>
                            {!sidebarCollapsed && <span className="nav-label">{item.label}</span>}
                        </Link>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <button 
                        className="nav-item logout-btn" 
                        onClick={handleLogout}
                        title={sidebarCollapsed ? 'Logout' : ''}
                    >
                        <i className='bx bx-log-out nav-icon'></i>
                        {!sidebarCollapsed && <span className="nav-label">Logout</span>}
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <div className="admin-main">
                {/* Top Bar */}
                <header className="admin-header">
                    <button className="sidebar-toggle desktop" onClick={toggleSidebar}>
                        <i className={`bx ${sidebarCollapsed ? 'bx-menu' : 'bx-menu-alt-left'}`}></i>
                    </button>
                    
                    <button className="sidebar-toggle mobile" onClick={toggleMobileMenu}>
                        <i className='bx bx-menu'></i>
                    </button>

                    <div className="header-right">
                        <div
                            className="admin-profile-section"
                            onClick={() => navigate('/admin/profile')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate('/admin/profile');
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            title="Your profile"
                            aria-label="Open your profile"
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem' }}
                        >
                            <div className="admin-info">
                                <span className="admin-name">{profileData?.fullName || user?.email?.split('@')[0] || 'Admin'}</span>
                                <span className="admin-role">Administrator</span>
                            </div>
                            <div className="admin-avatar">
                                {profileData?.profileImage ? (
                                    <img 
                                        src={profileData.profileImage} 
                                        alt="Profile" 
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '0.5rem',
                                            objectFit: 'cover'
                                        }}
                                    />
                                ) : (
                                    <i className='bx bx-user-circle'></i>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Content Area */}
                <main className="admin-content">
                    {children}
                </main>
            </div>

            {/* Mobile Overlay */}
            {mobileMenuOpen && (
                <div className="mobile-overlay" onClick={toggleMobileMenu}></div>
            )}
        </div>
    );
});

AdminLayout.displayName = 'AdminLayout';

export default AdminLayout;
